/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */

/**
 * The radius, in the user's unit (miles or kilometers), the current theme's
 * vicinity search circle starts at when a point is placed on the map.
 */
export const VICINITY_INITIAL_RADIUS = 1;

// defines the values the radial's edge point is placed from
export type RadialEdgeParams = {
  latitude: number;
  longitude: number;
  radius: number;
  degreesPerUnit: number;
  isCurrent: boolean;
};

/**
 * Returns where the radial's edge point (its drag handle) is placed for a
 * radius, given the degrees one unit of that radius spans. The legacy theme
 * offsets the edge both north and east by the radius - a diagonal that
 * draws the circle about 1.41 times the requested radius - while the current
 * theme offsets it north only, so the drawn circle matches the radius.
 *
 * ```typescript
 *
 *   import {getRadialEdge} from '@ion/dls/src/maps/geofences/radialDrawing';
 *   ...
 *
 *   const {newLatitude, newLongitude} = getRadialEdge({
 *     latitude       : 33.68,
 *     longitude      : -117.82,
 *     radius         : 1,
 *     degreesPerUnit : getMilesInDegree(1),
 *     isCurrent      : true
 *   });
 *
 * ```
 *
 * @param   latitude       The circle center's latitude.
 * @param   longitude      The circle center's longitude.
 * @param   radius         The radius, in the unit `degreesPerUnit` measures.
 * @param   degreesPerUnit The degrees one unit of the radius spans.
 * @param   isCurrent      `true` for the current theme.
 * @returns                The edge point's `newLatitude` and `newLongitude`.
 */
export const getRadialEdge = ({
  latitude,
  longitude,
  radius,
  degreesPerUnit,
  isCurrent
}: RadialEdgeParams) => {
  const offset = radius * degreesPerUnit;

  return {
    newLatitude  : latitude + offset,
    newLongitude : isCurrent ? longitude : longitude + offset / Math.cos(latitude * (Math.PI / 180))
  };
};
